import { ChakraBox, ChakraFlex, Text } from '@equip.health/ui';
import { useRef, useState } from 'react';
import {
  DROPZONE_OVERLAY_TEXT,
  DROPZONE_SUPPORTED_FILE_TYPES_TEXT,
} from '~/lib/constants/documentCenter.constants';

interface DocumentDropZoneProps {
  children?: React.ReactNode;
  isDisabled?: boolean;
  onFilesDrop: (files: File[]) => void;
}

const DocumentDropZone = ({
  children,
  isDisabled,
  onFilesDrop,
}: DocumentDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const onDragEnter = (e: React.DragEvent) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();

    const hasFiles = Array.from(e.dataTransfer?.types || []).includes('Files');
    if (!hasFiles) return;

    dragCounter.current += 1;
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current -= 1;

    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e: React.DragEvent) => {
    if (isDisabled) return;
    e.preventDefault();
    e.stopPropagation();

    dragCounter.current = 0;
    setIsDragging(false);

    onFilesDrop(Array.from(e.dataTransfer.files));
  };

  return (
    <ChakraBox
      data-testid="patient-document-dropzone"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      position="relative"
    >
      {children}

      {!isDisabled && isDragging && (
        <ChakraBox
          background="rgba(244, 247, 255, 0.80)"
          borderColor="accent.periwinkle"
          borderRadius="16px"
          borderStyle="dashed"
          borderWidth="1px"
          inset="0"
          pointerEvents="none"
          position="absolute"
          zIndex="1"
        >
          <ChakraFlex
            align="center"
            direction="column"
            gap="12px"
            height="100%"
            justify="center"
          >
            <Text color="neutral.secondary" variant="h5">
              {DROPZONE_OVERLAY_TEXT}
            </Text>
            <Text color="neutral.secondary" variant="body">
              {DROPZONE_SUPPORTED_FILE_TYPES_TEXT}
            </Text>
          </ChakraFlex>
        </ChakraBox>
      )}
    </ChakraBox>
  );
};

export default DocumentDropZone;
